import type { ChoiceField, ChoiceOption } from "./types";

export type MagicInitiateSource = {
  readonly id: string;
  readonly label: string;
  readonly cantrips: readonly ChoiceOption[];
  readonly firstLevelSpells: readonly ChoiceOption[];
};

export type MagicInitiateConfig = {
  readonly sourceClass: string;
  readonly cantrips: readonly string[];
  readonly spell: string;
  readonly ability: string;
};

/** SRD 5.2.1: "Intelligence, Wisdom, or Charisma is your spellcasting ability
 * for this feat's spells (choose when you select this feat)" — an independent
 * choice, NOT derived from the source class. */
export const MAGIC_INITIATE_ABILITIES = new Set(["int", "wis", "cha"]);

export type MagicInitiateValidation =
  | { readonly ok: true; readonly config: MagicInitiateConfig }
  | {
      readonly ok: false;
      readonly reason:
        | "invalid-shape"
        | "unknown-source"
        | "wrong-cantrip-count"
        | "duplicate-cantrips"
        | "invalid-cantrip"
        | "invalid-spell"
        | "invalid-ability";
    };

export type MagicInitiateReplacementValidation =
  | { readonly ok: true; readonly config: MagicInitiateConfig }
  | {
      readonly ok: false;
      readonly reason:
        | "invalid-existing-config"
        | "unknown-source"
        | "invalid-spell"
        | "same-spell";
    };

export const MAGIC_INITIATE_CHOICES: readonly MagicInitiateSource[] = [
  {
    id: "cleric",
    label: "Cleric",
    cantrips: [
      { id: "action_guidance", label: "Guidance" },
      { id: "action_resistance_spell", label: "Resistance" },
      { id: "action_sacred_flame", label: "Sacred Flame" },
    ],
    firstLevelSpells: [
      { id: "action_bless", label: "Bless" },
      { id: "action_cure_wounds", label: "Cure Wounds" },
      { id: "action_guiding_bolt", label: "Guiding Bolt" },
      { id: "action_healing_word", label: "Healing Word" },
      { id: "action_inflict_wounds", label: "Inflict Wounds" },
    ],
  },
  {
    id: "sorcerer",
    label: "Sorcerer",
    cantrips: [
      { id: "action_acid_splash", label: "Acid Splash" },
      { id: "action_fire_bolt", label: "Fire Bolt" },
      { id: "action_poison_spray", label: "Poison Spray" },
      { id: "action_ray_of_frost", label: "Ray of Frost" },
      { id: "action_shocking_grasp", label: "Shocking Grasp" },
    ],
    firstLevelSpells: [
      { id: "action_burning_hands", label: "Burning Hands" },
      { id: "action_chromatic_orb", label: "Chromatic Orb" },
      { id: "action_ice_knife", label: "Ice Knife" },
      { id: "action_ray_of_sickness", label: "Ray of Sickness" },
      { id: "action_thunderwave", label: "Thunderwave" },
    ],
  },
  {
    id: "wizard",
    label: "Wizard",
    cantrips: [
      { id: "action_fire_bolt", label: "Fire Bolt" },
      { id: "action_light", label: "Light" },
      { id: "action_ray_of_frost", label: "Ray of Frost" },
      { id: "action_shocking_grasp", label: "Shocking Grasp" },
    ],
    firstLevelSpells: [
      { id: "action_burning_hands", label: "Burning Hands" },
      { id: "action_chromatic_orb", label: "Chromatic Orb" },
      { id: "action_color_spray", label: "Color Spray" },
      { id: "action_ice_knife", label: "Ice Knife" },
      { id: "action_thunderwave", label: "Thunderwave" },
    ],
  },
] as const;

const CANTRIP_IDS_BY_SOURCE = new Map(
  MAGIC_INITIATE_CHOICES.map((source) => [
    source.id,
    new Set(source.cantrips.map((cantrip) => cantrip.id)),
  ])
);

const FIRST_LEVEL_IDS_BY_SOURCE = new Map(
  MAGIC_INITIATE_CHOICES.map((source) => [
    source.id,
    new Set(source.firstLevelSpells.map((spell) => spell.id)),
  ])
);

const SOURCE_LOOKUP = new Map(
  MAGIC_INITIATE_CHOICES.map((source) => [source.id, source])
);

const SOURCE_OPTIONS = MAGIC_INITIATE_CHOICES.map(({ id, label }) => ({
  id,
  label,
}));

function buildChoiceRevealMap(): Readonly<
  Record<string, readonly ChoiceField[]>
> {
  const reveal: Record<string, readonly ChoiceField[]> = {};

  for (const source of MAGIC_INITIATE_CHOICES) {
    reveal[source.id] = [
      {
        kind: "multi",
        key: "cantrips",
        label: "Cantrips",
        count: 2,
        options: source.cantrips,
      },
      {
        kind: "single",
        key: "spell",
        label: "First-level spell",
        options: source.firstLevelSpells,
      },
      // SRD 5.2.1: the spellcasting ability is an independent int/wis/cha
      // choice made when selecting the feat (NOT derived from the source
      // class). Required by validateMagicInitiateConfig since PR #2393 —
      // without this field the wizard UI cannot produce a valid config.
      {
        kind: "single",
        key: "ability",
        label: "Spellcasting ability",
        options: [
          { id: "int", label: "Intelligence" },
          { id: "wis", label: "Wisdom" },
          { id: "cha", label: "Charisma" },
        ],
      },
    ];
  }

  return reveal;
}

export function magicInitiateChoiceFields(): readonly ChoiceField[] {
  return [
    {
      kind: "dependent",
      key: "sourceClass",
      label: "Source class",
      options: SOURCE_OPTIONS,
      reveal: buildChoiceRevealMap(),
    },
  ];
}

function isStringRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is readonly string[] {
  return (
    Array.isArray(value) &&
    value.every(
      (candidate): candidate is string => typeof candidate === "string"
    )
  );
}

function hasValidShape(value: unknown): value is {
  sourceClass: string;
  cantrips: readonly string[];
  spell: string;
} {
  if (!isStringRecord(value)) {
    return false;
  }

  const record: Record<string, unknown> = value;

  return (
    typeof record.sourceClass === "string" &&
    isStringArray(record.cantrips) &&
    typeof record.spell === "string"
  );
}

export function validateMagicInitiateConfig(
  config: unknown
): MagicInitiateValidation {
  if (!hasValidShape(config)) {
    return { ok: false, reason: "invalid-shape" };
  }

  const source = SOURCE_LOOKUP.get(config.sourceClass);
  if (!source) {
    return { ok: false, reason: "unknown-source" };
  }

  if (config.cantrips.length !== 2) {
    return { ok: false, reason: "wrong-cantrip-count" };
  }

  if (new Set(config.cantrips).size !== 2) {
    return { ok: false, reason: "duplicate-cantrips" };
  }

  const cantrips = CANTRIP_IDS_BY_SOURCE.get(source.id);
  if (!cantrips || !config.cantrips.every((id) => cantrips.has(id))) {
    return { ok: false, reason: "invalid-cantrip" };
  }

  const spells = FIRST_LEVEL_IDS_BY_SOURCE.get(source.id);
  if (!spells || !spells.has(config.spell)) {
    return { ok: false, reason: "invalid-spell" };
  }

  const ability = (config as { ability?: unknown }).ability;
  if (typeof ability !== "string" || !MAGIC_INITIATE_ABILITIES.has(ability)) {
    return { ok: false, reason: "invalid-ability" };
  }

  return {
    ok: true,
    config: {
      sourceClass: source.id,
      cantrips: [...config.cantrips],
      spell: config.spell,
      ability,
    },
  };
}

export function validateMagicInitiateReplacement(
  existing: unknown,
  replacementSpell: string
): MagicInitiateReplacementValidation {
  const current = validateMagicInitiateConfig(existing);
  if (!current.ok) {
    return current.reason === "unknown-source"
      ? { ok: false, reason: "unknown-source" }
      : { ok: false, reason: "invalid-existing-config" };
  }

  const spells = FIRST_LEVEL_IDS_BY_SOURCE.get(current.config.sourceClass);
  if (!spells) {
    return { ok: false, reason: "unknown-source" };
  }

  if (!spells.has(replacementSpell)) {
    return { ok: false, reason: "invalid-spell" };
  }

  if (replacementSpell === current.config.spell) {
    return { ok: false, reason: "same-spell" };
  }

  return {
    ok: true,
    config: {
      sourceClass: current.config.sourceClass,
      cantrips: [...current.config.cantrips],
      spell: replacementSpell,
      ability: current.config.ability,
    },
  };
}
